/**
 * Citizen Level Badge Assets
 *
 * Badge images + display copy for the 5 citizen levels.
 *
 * NOTE: the miles thresholds and level-from-miles math are NOT defined here —
 * they are canonical in `@tonnie/heroes` (`CITIZEN_LEVEL_THRESHOLDS`,
 * `calculateCitizenLevel`, `getProgressToNextLevel`). This file owns ONLY the
 * presentation layer (badge SVGs, names, descriptions). Do not re-introduce
 * threshold numbers or level math here — that's the drift the data-integrity
 * suite guards against.
 */

import type { AssetPath, CitizenLevel } from '../types';

/**
 * Base path for citizen level badges
 */
const CITIZEN_BASE = '/assets/badges/citizen';

/**
 * Citizen level names
 */
export const CITIZEN_LEVEL_NAMES: Record<CitizenLevel, string> = {
  1: 'Newcomer',
  2: 'Explorer',
  3: 'Voyager',
  4: 'Pioneer',
  5: 'Ambassador',
} as const;

/**
 * Citizen level descriptions
 */
export const CITIZEN_LEVEL_DESCRIPTIONS: Record<CitizenLevel, string> = {
  1: 'Just starting your journey',
  2: 'Beginning to explore new horizons',
  3: 'Making significant progress',
  4: 'Leading the way forward',
  5: 'A true global citizen',
} as const;

/**
 * Citizen level badge assets
 */
export const CITIZEN_LEVEL_BADGES: Record<CitizenLevel, AssetPath> = {
  1: {
    path: `${CITIZEN_BASE}/level-1-newcomer.svg`,
    alt: 'Newcomer Badge - Citizen Level 1',
    width: 100,
    height: 100,
  },
  2: {
    path: `${CITIZEN_BASE}/level-2-explorer.svg`,
    alt: 'Explorer Badge - Citizen Level 2',
    width: 100,
    height: 100,
  },
  3: {
    path: `${CITIZEN_BASE}/level-3-voyager.svg`,
    alt: 'Voyager Badge - Citizen Level 3',
    width: 100,
    height: 100,
  },
  4: {
    path: `${CITIZEN_BASE}/level-4-pioneer.svg`,
    alt: 'Pioneer Badge - Citizen Level 4',
    width: 100,
    height: 100,
  },
  5: {
    path: `${CITIZEN_BASE}/level-5-ambassador.svg`,
    alt: 'Ambassador Badge - Citizen Level 5',
    width: 100,
    height: 100,
  },
} as const;

/**
 * Get citizen level badge path
 */
export function getCitizenLevelBadge(level: CitizenLevel): string {
  return CITIZEN_LEVEL_BADGES[level].path;
}

/**
 * Get citizen level badge asset with metadata
 */
export function getCitizenLevelBadgeAsset(level: CitizenLevel): AssetPath {
  return CITIZEN_LEVEL_BADGES[level];
}

/**
 * Get citizen level name
 */
export function getCitizenLevelName(level: CitizenLevel): string {
  return CITIZEN_LEVEL_NAMES[level];
}

/**
 * Get citizen level description
 */
export function getCitizenLevelDescription(level: CitizenLevel): string {
  return CITIZEN_LEVEL_DESCRIPTIONS[level];
}

// Level-from-miles math intentionally lives in `@tonnie/heroes`
// (calculateCitizenLevel / getProgressToNextLevel / CITIZEN_LEVEL_THRESHOLDS).
// Import from there — do not redefine thresholds in this assets package.
